import { Fragment, useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XCircleIcon,
} from "@heroicons/react/24/solid";
import { XMarkIcon } from "@heroicons/react/20/solid";

export default function Sucessnotify({
    type = "success",
    message = "Completed successfully!",
    duration = 3000,
    onClose,
}) {
    const [show, setShow] = useState(true);
    const [hovered, setHovered] = useState(false);
    const [timer, setTimer] = useState(null);

    useEffect(() => {
        const timerId = setTimeout(() => {
            if (!hovered) {
                setShow(false);
                if (onClose) onClose();
            }
        }, duration);

        setTimer(timerId);

        return () => {
            clearTimeout(timerId);
        };
    }, [duration, hovered, onClose]);

    const handleMouseEnter = () => {
        setHovered(true);
        if (timer) {
            clearTimeout(timer);
        }
    };

    const handleMouseLeave = () => {
        setHovered(false);
        setTimeout(() => {
            setShow(false);
            if (onClose) onClose();
        }, 3000);
    };

    const notificationStyles = {
        success: {
            icon: (
                <CheckCircleIcon className="w-5 h-5 sm:h-7 sm:w-7 text-green-400" aria-hidden="true"
                />
            ),
            borderColor: "border-green-500",
        },
        warning: {
            icon: (
                <ExclamationTriangleIcon className="h-7 w-7 text-yellow-400" aria-hidden="true" />
            ),
            borderColor: "border-yellow-500",
        },
        error: {
            icon: (
                <XCircleIcon className="h-7 w-7 text-red-400" aria-hidden="true" />
            ),
            borderColor: "border-red-500",
        },
        info: {
            icon: (
                <InformationCircleIcon className="h-7 w-7 text-blue-400" aria-hidden="true" />
            ),
            borderColor: "border-blue-500",
        },
    };

    const { icon, borderColor } =
        notificationStyles[type] || notificationStyles.success;

    return (
        <Transition show={show} as={Fragment} enter="transform ease-out duration-300 transition" enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2" enterTo="translate-y-0 opacity-100 sm:translate-x-0" leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">

            <div className={`pointer-events-auto w-[60%] sm:w-full h-[58px] sm:h-[65px] max-w-xs sm:max-w-sm overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white transition-all duration-300 transform fixed top-4 right-4 sm:right-6 ${show ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`} style={{ "--duration": `${duration}ms`, }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} >
                <div className="p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">{icon}</div>
                        <div className="ml-3 w-0 flex-1 pt-0.5">
                            <p className={`text-sm sm:text-base font-semibold text-black`}>
                                {message}
                            </p>
                        </div>
                        <div className="ml-4 flex flex-shrink-0">
                            <button type="button" className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2" onClick={(event) => { event.preventDefault(); event.stopPropagation(); setShow(false); if (onClose) onClose(); }} >
                                <span className="sr-only">Close</span>
                                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                            </button>

                        </div>
                    </div>
                </div>
                <div className={`border-b-[6px] ${borderColor} animate-shrink`}></div>
            </div>
        </Transition>
    );
}
